/**
 * bootChatMode — how the chat will look on its FIRST frame (open, docked,
 * full screen), read without loading the chat engine.
 *
 * WHY THIS IS ITS OWN FILE (PR-S, 2026-09-22). The chat engine now loads a
 * moment after the page paints (Miles: "slim the app first"). A returning
 * visitor whose chat was docked open expects the page laid out beside it from
 * the start; a phone whose chat was open expects the full-screen takeover, not
 * the landing page and then a cover. The engine's reducer decided both from
 * storage at its first render — which is now too late — so the reading moves
 * here, verbatim, and BOTH read it: ChatEngineLoader stamps the page before
 * the engine exists, and the engine's reducer initialises from the same
 * function. One reading, so the frame before the engine and the engine's first
 * frame can never disagree.
 *
 * Leaf only — it must never import the engine (scripts/check-chat-first-paint.mjs).
 */

import { MOBILE_VIEWPORT_QUERY } from '../../styles/breakpoints';
import {
  GLOBAL_CHAT_OPEN_KEY,
  GLOBAL_CHAT_DOCK_KEY,
  GLOBAL_CHAT_FULLSCREEN_KEY,
  buildPageStorageKey,
} from './types';
import type { GlobalChatConfig } from './types';
import { getFromLocalStorage } from './utils';
import { normalizeChatMode, readMinimizedSpot } from './chatModeRules';
import type { ChatMinimizedSpot } from './chatModeRules';
import { readTabMode } from './chatModeMemory';

export interface BootChatMode {
  isChatOpen: boolean;
  isMobileViewport: boolean;
  isChatDockedRight: boolean;
  isChatFullScreen: boolean;
  /** Where the closed chat rests on a desktop (chatModeRules.ts). */
  chatMinimizedSpot: ChatMinimizedSpot;
}

export type BootChatModeConfig = Partial<Pick<GlobalChatConfig, 'initialOpen' | 'initialDocked' | 'disableDocking'>>;

/**
 * Initialise from storage, so the chat never flashes open or closed: this tab's
 * own memory first (a reload, or a tab a link opened, chatModeMemory.ts), else
 * the device's default, the person's own last choice.
 */
export function readBootChatMode(config: BootChatModeConfig): BootChatMode {
  let initialOpen = Boolean(config.initialOpen);
  let initialDock = Boolean(config.initialDocked);
  const tab = typeof window !== 'undefined' ? readTabMode() : null;

  if (typeof window !== 'undefined') {
    try {
      const pageOpenKey = buildPageStorageKey(GLOBAL_CHAT_OPEN_KEY);
      const storedOpen = getFromLocalStorage<string>(pageOpenKey, initialOpen ? '1' : '0');
      initialOpen = tab ? tab.open : storedOpen === '1';

      if (!config.disableDocking) {
        const pageDockKey = buildPageStorageKey(GLOBAL_CHAT_DOCK_KEY);
        const storedDock = getFromLocalStorage<string>(pageDockKey, initialDock ? '1' : '0');
        initialDock = storedDock === '1';
      }
    } catch {
      // fall back to defaults
    }
  }

  // Seed the mobile bit HERE (not in the module-level initialState, which
  // unit tests import without a matchMedia stub) so a phone reload that
  // restores an open chat boots straight into the fullscreen takeover with
  // no floating-panel flash before the viewport effect's first dispatch.
  let initialMobile = false;
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    try {
      initialMobile = window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
    } catch {
      initialMobile = false;
    }
  }

  const storedFullscreen = tab
    ? tab.fullScreen
    : typeof window !== 'undefined'
      ? getFromLocalStorage<string>(buildPageStorageKey(GLOBAL_CHAT_FULLSCREEN_KEY), '0') === '1'
      : false;

  // The first frame obeys the same rule as every later one (2026-10-08): on a
  // desktop the chat is docked exactly when it is not full screen, so a device
  // that stored the retired centred pane (open, not docked, not full screen)
  // boots onto the docked rail.
  return {
    ...normalizeChatMode({
      isChatOpen: initialOpen,
      isMobileViewport: initialMobile,
      isChatDockedRight: initialMobile ? false : initialDock,
      isChatFullScreen: (initialMobile && initialOpen) ? true : storedFullscreen,
    }),
    chatMinimizedSpot: tab ? tab.spot : readMinimizedSpot(),
  };
}

/**
 * What the page should carry before the engine exists — the same two facts
 * the engine publishes once it runs (GlobalChatSystem.tsx: the docked stamp on
 * <html> and the full-screen flag on chatPanelStore), from the same inputs.
 */
export function bootPageSignals(mode: BootChatMode, hidden: boolean): { docked: boolean; fullScreen: boolean } {
  return {
    docked: mode.isChatOpen && mode.isChatDockedRight && !mode.isChatFullScreen && !hidden,
    fullScreen: mode.isChatOpen && mode.isChatFullScreen && !hidden,
  };
}
